import React, { useState } from "react";
import { Box, Divider, alpha, useTheme, Button } from "@mui/material";
import Text from "components/mui/Text";
import { typography } from "theme";
import { getOrderStatus } from "../../utils/OrderStatus";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ServiceRequestContainer from "containers/ServiceRequestContainer";
import toast from "react-hot-toast";

const FinfactorOrderDetailsLayout = ({ data }) => {
  const theme = useTheme();
  const [logoError, setLogoError] = useState(false);

  if (!data) return null;

  const statusInfo = getOrderStatus(data.status, theme);

  const handleCopyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy to clipboard");
    }
  };

  const renderIcon = (status) => {
    const { Icon, color } = getOrderStatus(status, theme);
    if (!Icon) return null;

    return (
      <Icon
        sx={{
          color,
          fontSize: 22,
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
        flexDirection: { xs: "column", lg: "row" },
      }}
    >
      {/* LEFT CARD */}
      <Box
        sx={{
          flex: 2,
          background: "#161B2B",
          borderRadius: "16px",
          p: 4,
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          {/* LOGO */}
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              background:
                data.logo && !logoError
                  ? "white"
                  : "linear-gradient(135deg,#46A8DC,#72B7E3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexShrink: 0,
              p: data.logo && !logoError ? 0.5 : 0,
            }}
          >
            {data.logo && !logoError ? (
              <img
                src={data.logo}
                alt={data.fundName}
                onError={() => setLogoError(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Text
                sx={{
                  color: "white",
                  fontSize: typography.fontSize.h5,
                  fontWeight: 400,
                }}
              >
                {data.fundName?.charAt(0).toUpperCase() || "F"}
              </Text>
            )}
          </Box>

          {/* NAME */}
          <Box sx={{ flex: 1 }}>
            <Text
              sx={{
                fontSize: { xs: 16, sm: 18 },
                fontWeight: 400,
                color: "white",
              }}
            >
              {data.fundName}
            </Text>
            <Text
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                fontSize: 13,
                mt: 0.5,
              }}
            >
              {data.type === "SIP" ? "SIP instalment" : data.type}
            </Text>
          </Box>

          {/* AMOUNT */}
          <Text
            sx={{
              fontSize: 24,
              fontWeight: 400,
              color: "white",
            }}
          >
            {data.formattedAmount || `₹${data.amount}`}
          </Text>
        </Box>

        <Divider
          sx={{
            my: 3,
            borderColor: alpha(theme.palette.common.white, 0.1),
          }}
        />

        {/* ORDER DETAILS */}
        <Text
          sx={{
            fontSize: 18,
            fontWeight: 400,
            color: "white",
            mb: 3,
          }}
        >
          Order Details
        </Text>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3,1fr)",
            },
            gap: 4,
          }}
        >
          {Object.entries(data.details || {}).map(([key, value]) => {
            const isCopyable =
              key.toLowerCase().includes("order id") ||
              key.toLowerCase().includes("folio no");

            return (
              <Box key={key}>
                <Text
                  sx={{
                    fontSize: 10,
                    color: "rgba(255, 255, 255, 0.5)",
                    mb: 0.5,
                    textTransform: "capitalize",
                  }}
                >
                  {key}
                </Text>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Text
                    sx={{
                      fontWeight: 400,
                      color: "white",
                      fontSize: 14,
                    }}
                  >
                    {value || "-"}
                  </Text>
                  {isCopyable && value !== "-" && (
                    <ContentCopyIcon
                      onClick={() => handleCopyToClipboard(value, key)}
                      sx={{
                        fontSize: 14,
                        color: "#0ea5e9",
                        cursor: "pointer",
                        "&:hover": { color: "white" },
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* RIGHT CARD */}
      <Box
        sx={{
          flex: 1,
          background: "#161B2B",
          borderRadius: "16px",
          p: 4,
          height: "fit-content",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        {/* STATUS HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 4,
          }}
        >
          <Text
            sx={{
              fontSize: 18,
              fontWeight: 400,
              color: "white",
            }}
          >
            Status
          </Text>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: statusInfo.color,
              }}
            />
            <Text
              sx={{
                color: statusInfo.color,
                fontWeight: 400,
                fontSize: 14,
              }}
            >
              {statusInfo.text}
            </Text>
          </Box>
        </Box>
        <Divider sx={{ mb: 4, borderColor: "rgba(255, 255, 255, 0.1)" }} />

        {/* TIMELINE */}
        <Box sx={{ position: "relative" }}>
          {data.steps?.map((step, index) => (
            <Box
              key={step.id}
              sx={{
                display: "flex",
                gap: 2.5,
                mb: index !== data.steps.length - 1 ? 4 : 0,
                position: "relative",
                alignItems: "center",
              }}
            >
              {/* LINE */}
              {index !== data.steps.length - 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 11,
                    top: 28,
                    bottom: -32,
                    width: "1.5px",
                    background: "rgba(255, 255, 255, 0.15)",
                  }}
                />
              )}
              {/* ICON */}
              <Box sx={{ mt: 0.2 }}>{renderIcon(step.status)}</Box>
              {/* TEXT */}
              <Box>
                <Text
                  sx={{
                    color: "white",
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  {step.title}
                </Text>
                <Text
                  sx={{
                    fontSize: 11,
                    color: "rgba(255, 255, 255, 0.5)",
                    mt: 0.2,
                  }}
                >
                  {step.timestamp}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <ServiceRequestContainer />
    </Box>
  );
};

export default FinfactorOrderDetailsLayout;
